import { CLASS_GROUPS } from "#constants/classOptions"
import {
  CoreSkillLevel,
  DigitalSkillLevel,
  SculptureSkillLevel,
} from "#constants/skillLevelConstants"
import { StandoutSummary } from "#customTypes/composedTypes/standoutTypes"
import { Uuid } from "#customTypes/globalTypes"
import { DatabaseObject, DataWithId, IsoDate } from "#customTypes/types"

export const PRINT_URL_FRESHNESS_MS = 14 * 60 * 1000 // 14 minutes (URLs expire at ~15 min)

export enum EVALUATION_STATUS {
  NOT_STARTED = "NOT_STARTED",
  IN_PROGRESS = "IN_PROGRESS",
  SUBMITTED = "SUBMITTED",
  APPROVED = "APPROVED",
  PRINTED = "PRINTED",
  REJECTED = "REJECTED",
  NO_EVAL = "NO_EVAL",
}

export const NO_PROMO_REASONS = [
  "behavior",
  "attendance",
  "proficiency",
] as const

export type NoPromoReason = (typeof NO_PROMO_REASONS)[number]

export const NO_PROMO_REASON_LABELS: Record<NoPromoReason, string> = {
  behavior: "Behavior",
  attendance: "Attendance",
  proficiency: "Proficiency",
}

export const EVAL_STATUS_SORT_ORDER = [
  EVALUATION_STATUS.REJECTED,
  EVALUATION_STATUS.NOT_STARTED,
  EVALUATION_STATUS.IN_PROGRESS,
  EVALUATION_STATUS.SUBMITTED,
  EVALUATION_STATUS.APPROVED,
  EVALUATION_STATUS.PRINTED,
  EVALUATION_STATUS.NO_EVAL,
]

export const EVAL_COLORS = {
  [EVALUATION_STATUS.NOT_STARTED]: "default",
  [EVALUATION_STATUS.IN_PROGRESS]: "warning",
  [EVALUATION_STATUS.SUBMITTED]: "orange",
  [EVALUATION_STATUS.APPROVED]: "success",
  [EVALUATION_STATUS.PRINTED]: "blue",
  [EVALUATION_STATUS.REJECTED]: "error",
  [EVALUATION_STATUS.NO_EVAL]: "red",
} as const

export enum EVALUATION_PROBLEM_SEVERITY {
  LOW = "LOW",
  MEDIUM = "MEDIUM",
  HIGH = "HIGH",
}

export type EvaluationProblemFlag = {
  isProblematic: boolean
  reasonCodes: string[]
  severity?: EVALUATION_PROBLEM_SEVERITY
}

export enum EvaluationTraitCategory {
  ART_SKILLS = "ART_SKILLS",
  SOCIAL_SKILLS = "SOCIAL_SKILLS",
  CREATIVITY = "CREATIVITY",
}

export type EvaluationTrait = {
  id: Uuid
  category: EvaluationTraitCategory
  name: string // This is the label
  description: string
  semesters: string[]
  skillLevels: string[]
  isActive: boolean

  createdAt: IsoDate
  createdBy: Uuid
  lastUpdatedAt: IsoDate
  lastUpdatedBy: Uuid
}

// New evaluation interface that matches the converted database structure
export interface EvaluationConstructor extends DataWithId {
  id: Uuid
  studentId: Uuid
  curriculumId: Uuid
  semester: string
  classGroup: CLASS_GROUPS
  classId: Uuid
  status: EVALUATION_STATUS

  printing?: {
    status: "done" | "error" | "processing" | "pending" | "printed"
    error?: string | null
    url?: string | null
    filename?: string | null
  }

  assignedTo?: Uuid
  assignedBy?: Uuid
  assignedAt?: string
  createdBy: Uuid
  createdAt: string
  savedBy?: Uuid
  savedAt?: string
  submittedBy?: Uuid
  submittedAt?: string
  approvedBy?: Uuid
  approvedAt?: string
  approverId?: Uuid
  rejectedBy?: Uuid
  rejectedAt?: string
  noPromoReason?: NoPromoReason[]
  noPromoText?: string
  lastUpdatedAt: string
  lastUpdatedBy: Uuid
  printedBy?: Uuid
  printedAt?: string

  didWell: string
  needsWork: string
  secretMessage?: string
  checkboxes: Record<string, boolean>
  currentSkill: CoreSkillLevel | DigitalSkillLevel | SculptureSkillLevel
  nextSkill: CoreSkillLevel | DigitalSkillLevel | SculptureSkillLevel

  standoutSummary?: StandoutSummary
}

export interface EvaluationForDisplay
  extends Omit<
    EvaluationConstructor,
    "classGroup" | "status" | "currentSkill" | "nextSkill" | "semester"
  > {
  id: Uuid
  studentId: Uuid
  classGroup: string
  status: string
  currentSkill: string
  nextSkill: string
  semester: string

  checkboxes: {
    name: string
    checked: boolean
    id: Uuid
    category: EvaluationTraitCategory
  }[]
  didWell: string
  needsWork: string
  secretMessage: string

  createdBy: string | undefined
  lastUpdatedBy: string | undefined
  approvedBy: string | undefined
  assignedBy: string | undefined
  assignedTo: string | undefined
  assignedToId: Uuid | undefined | null
  savedBy: string | undefined
  submittedBy: string | undefined
  printedBy: string | undefined
}

// Legacy evaluation interface from the old system
export interface OldStudentEvaluation {
  id: Uuid
  semester: string
  semesterPlan: Uuid
  status: string
  group: string

  createdAt: IsoDate
  createdBy: Uuid
  lastUpdatedAt: IsoDate
  lastUpdatedBy: Uuid
  submittedAt?: IsoDate
  submittedBy?: Uuid
  approvedAt?: IsoDate
  approvedBy?: Uuid

  // Deprecated fields - TODO: Fix this in the db converter
  savedBy: Uuid
  timeSaved: IsoDate
  timeSubmitted?: IsoDate
  timeApproved?: IsoDate

  checkedBoxes: Record<Uuid, boolean>
  currentSkill: string
  nextSkill: string
  badSentences: string
  goodSentences: string
  secretMessage: string
}

// If a student has 2 of the same class group, they will have 1 eval, but this is very rare
export interface Evaluation extends DataWithId {
  id: Uuid
  studentId: Uuid

  assignedTo?: Uuid // Teacher who created it
  assignedBy?: Uuid
  assignedAt?: IsoDate
  createdBy: Uuid
  createdAt: IsoDate
  savedBy?: Uuid
  savedAt?: IsoDate
  submittedBy?: Uuid
  submittedAt?: IsoDate
  approvedBy?: Uuid
  approvedAt?: IsoDate
  approverId?: Uuid
  printedBy?: Uuid
  printedAt?: IsoDate
  lastUpdatedAt: IsoDate
  lastUpdatedBy: Uuid

  didWell: string // renamed from goodSentences
  needsWork: string // renamed from badSentences
  secretMessage?: string
  checkboxes: Record<Uuid, boolean> // renamed from checkedBoxes

  semester: string
  classId: Uuid
  curriculumId: Uuid // renamed from semesterPlan
  // classId?: Uuid // TODO: Add this back in
  classGroup: CLASS_GROUPS // renamed from group
  status: EVALUATION_STATUS
  currentSkill: string
  nextSkill: string

  standoutSummary?: StandoutSummary
}

// Interface for creating new evaluations (metadata fields added automatically)
export interface CreateEvaluationData extends DatabaseObject {
  studentId: Uuid
  assignedTo?: Uuid | null
  assignedBy?: Uuid | null
  assignedAt?: string
  classGroup: CLASS_GROUPS
  classId: Uuid
  status: EVALUATION_STATUS
  semester: string
  curriculumId: Uuid
}

export type PrintEvalCurrentCheckboxes = {
  currentarttext1: string
  currentartcheck1: boolean
  currentarttext2: string
  currentartcheck2: boolean
  currentarttext3: string
  currentartcheck3: boolean

  currentcreativitytext1: string
  currentcreativitycheck1: boolean
  currentcreativitytext2: string
  currentcreativitycheck2: boolean

  currentsocialtext1: string
  currentsocialcheck1: boolean
  currentsocialtext2: string
  currentsocialcheck2: boolean
}

export type PrintEvalNextCheckboxes = {
  nextarttext1: string
  nextartcheck1: boolean
  nextarttext2: string
  nextartcheck2: boolean
  nextarttext3: string
  nextartcheck3: boolean

  nextcreativitytext1: string
  nextcreativitycheck1: boolean
  nextcreativitytext2: string
  nextcreativitycheck2: boolean

  nextsocialtext1: string
  nextsocialcheck1: boolean
  nextsocialtext2: string
  nextsocialcheck2: boolean
  nextsocialtext3?: string
  nextsocialcheck3?: boolean
}

export type PrintEvalData = {
  studentname: string
  currentlevel: string
  nextlevel: string
  thumbsup?: string // didWell
  needstoworkon?: string // needsWork
  secretmessage?: string
  icon?: { url: string }
} & PrintEvalCurrentCheckboxes &
  PrintEvalNextCheckboxes
